import { memo, Suspense, useState } from 'react';
import { AlertTriangle, GripVertical, Settings, X, Eye, EyeOff } from 'lucide-react';
import { WidgetErrorBoundary } from '../../components/dashboard';
import {
  TicketVolumeWidget,
  TicketsByStatusWidget,
  SLAComplianceWidget,
  AgentPerformanceWidget,
  RecentActivityWidget,
  ChannelDistributionWidget,
  AutomationHealthWidget,
  MyPerformanceWidget,
  MyQueueWidget,
  ResolutionSourceWidget,
} from '../../components/dashboard';
import { KpiCardWidget, CustomMetricWidget, WidgetCommonProps } from './widgets';
import { useThemeColors } from '../../components/charts/useThemeColors';
import { DashboardWidget } from '../../services/dashboards.service';
import { normalizeResolutionSourceTitle, widgetDisplayTitle } from './widgetTitles';

export interface WidgetRendererProps {
  widget: DashboardWidget;
  dashboardData?: any;
  agentData?: any;
  slaData?: any;
  trendsData?: any[];
  recentActivityData?: any[];
  loading?: boolean;
  dateRange?: { start: string; end: string };
  isEditMode?: boolean;
  onRemove?: (widgetId: string) => void;
  onToggleVisibility?: (widgetId: string, visible: boolean) => void;
  onConfigure?: (widgetId: string) => void;
  /** Available chart types for this widget (passed from catalog) */
  chartTypes?: string[];
  /** Callback when admin changes chart type inline */
  onChartTypeChange?: (widgetId: string, chartType: string) => void;
  /** Live Dashboard only: KPI cards open the tickets behind their figure. */
  drillDown?: { teamId?: string };
}

/**
 * Semantic palettes, in the project's own tokens rather than Tailwind hexes.
 *
 * Severity ramps down the theme's warning tones (burgundy → terracotta) so a
 * chart agrees with the KPI cards and the SLA columns elsewhere; categories
 * that carry no severity (channels) use a neutral sequence instead of a
 * rainbow, because colouring "phone" red says something the data does not.
 *
 * These are TOKEN NAMES, resolved to concrete values by the component below —
 * recharts writes colours into SVG attributes, where `var(--co-*)` is dead.
 */
const PALETTE_TOKENS = {
  new: '--co-ink',
  inProgress: '--co-machine',
  pending: '--co-terracotta',
  resolved: '--co-green',
  closed: '--co-ink-4',

  urgent: '--co-burgundy',
  high: '--co-terracotta',
  medium: '--co-machine',
  low: '--co-ink-4',

  ch0: '--co-ink',
  ch1: '--co-machine',
  ch2: '--co-ink-3',
  ch3: '--co-brass',
  ch4: '--co-green',
} as const;

type Palette = Record<keyof typeof PALETTE_TOKENS, string>;

/** Severity name → its tone. Anything unrecognised stays neutral. */
function priorityColor(name: string, palette: Palette): string {
  switch (name.toLowerCase()) {
    case 'urgent':
      return palette.urgent;
    case 'high':
      return palette.high;
    case 'medium':
      return palette.medium;
    case 'low':
      return palette.low;
    default:
      return palette.ch2;
  }
}

/** Maps widget type strings to React components */
function renderWidgetContent(
  widgetType: string,
  props: WidgetCommonProps,
  extra: { agentData?: any; slaData?: any; dashboardData?: any; trendsData?: any[]; recentActivityData?: any[]; palette: Palette },
) {
  const { loading, dashboardData } = props;

  switch (widgetType) {
    // KPI cards
    case 'kpi_open_tickets':
    case 'kpi_total_tickets':
    case 'kpi_avg_response_time':
    case 'kpi_sla_compliance':
    case 'kpi_csat_score':
    case 'kpi_ai_resolution_rate':
      return (
        <KpiCardWidget
          {...props}
          slaData={extra.slaData}
          config={{ ...props.config, kpiType: widgetType, title: props.config?.title }}
        />
      );

    // Chart widgets — reuse existing components, support chartType config
    case 'chart_ticket_volume': {
      const volumeData = extra.trendsData?.map((t: any) => ({
        name: t.period || t.date || '',
        created: t.created ?? t.ticketsCreated ?? 0,
        resolved: t.resolved ?? t.ticketsResolved ?? 0,
      }));
      return <TicketVolumeWidget loading={loading} data={volumeData} chartType={props.config?.chartType} title={props.config?.title} />;
    }

    case 'chart_tickets_by_status': {
      let statusData: { name: string; value: number; color: string }[] | undefined;
      if (dashboardData) {
        const t = dashboardData.tickets || {};
        // Backtracked (archived) tickets were resolved before being archived, so
        // count them — and any other non-open remainder in the total — as
        // Resolved rather than a generic "Other" slice.
        const known = (t.new || 0) + (t.in_progress || 0) + (t.pending || 0) + (t.resolved || 0) + (t.closed || 0);
        const remainder = Math.max(0, (t.total || 0) - known);
        statusData = [
          { name: 'New', value: t.new || 0, color: extra.palette.new },
          { name: 'In Progress', value: t.in_progress || 0, color: extra.palette.inProgress },
          { name: 'Pending', value: t.pending || 0, color: extra.palette.pending },
          { name: 'Resolved', value: (t.resolved || 0) + remainder, color: extra.palette.resolved },
          { name: 'Closed', value: t.closed || 0, color: extra.palette.closed },
        ].filter(s => s.value > 0);
      }
      return <TicketsByStatusWidget loading={loading} data={statusData} chartType={props.config?.chartType} title={props.config?.title} />;
    }

    case 'chart_tickets_by_priority': {
      const priorityData = dashboardData?.priorities
        ? Object.entries(dashboardData.priorities).map(([name, value]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            value: value as number,
            color: priorityColor(name, extra.palette),
          }))
        : undefined;
      return <TicketsByStatusWidget loading={loading} data={priorityData} chartType={props.config?.chartType} title={props.config?.title || 'Tickets by Priority'} />;
    }

    case 'chart_channel_distribution': {
      const channelIcons = ['email', 'chat', 'phone', 'whatsapp', 'social'] as const;
      // Use channelsTotal (includes backtracked/archived) so this chart is
      // consistent with the Total Tickets and Tickets-by-Status boxes, which
      // also count backtracked. Fall back to the legacy set if absent.
      const channelSource = dashboardData?.channelsTotal || dashboardData?.channels;
      const channelData = channelSource
        ? Object.entries(channelSource).map(([name, value], i) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            value: value as number,
            color: [
              extra.palette.ch0,
              extra.palette.ch1,
              extra.palette.ch2,
              extra.palette.ch3,
              extra.palette.ch4,
            ][i % 5],
            icon: (channelIcons.find(ic => name.toLowerCase().includes(ic)) || 'email') as any,
          }))
        : undefined;
      return <ChannelDistributionWidget loading={loading} data={channelData} chartType={props.config?.chartType} title={props.config?.title} />;
    }

    case 'chart_sla_compliance': {
      const slaData = extra.slaData;
      let slaWidgetData;
      if (slaData) {
        const fr = slaData.firstResponse;
        const res = slaData.resolution;
        // Headline overall must MATCH the "SLA Compliance" KPI tile (which shows
        // the backend compliance_rate — a blend that already skips a missing FR
        // or resolution set instead of dragging to 0%). Fall back to a
        // count-weighted overall only if that figure isn't available.
        const totalMet = (fr?.compliant || 0) + (res?.compliant || 0);
        const totalObligations =
          (fr?.compliant || 0) + (fr?.breached || 0) + (res?.compliant || 0) + (res?.breached || 0);
        const countWeighted = totalObligations > 0 ? (totalMet / totalObligations) * 100 : 0;
        const overall = dashboardData?.performance?.firstResponseSlaCompliance ?? countWeighted;
        slaWidgetData = {
          overallCompliance: overall,
          metrics: [
            { name: 'First Response', value: fr?.percentage || 0, target: 95 },
            { name: 'Resolution', value: res?.percentage || 0, target: 90 },
          ],
        };
      }
      return (
        <SLAComplianceWidget
          loading={loading}
          overallCompliance={slaWidgetData?.overallCompliance}
          metrics={slaWidgetData?.metrics}
          title={props.config?.title}
        />
      );
    }

    // Agent widgets
    case 'agent_leaderboard': {
      const agentWidgetData = extra.agentData?.length
        ? [...extra.agentData]
            // Sort by tickets resolved (desc) so "Top 5" is truly the top, not
            // just the first 5 in whatever order the backend returned. Copy first
            // to avoid mutating the React Query cache.
            .sort((a: any, b: any) => (b.ticketsResolved || 0) - (a.ticketsResolved || 0))
            .slice(0, 5)
            .map((a: any) => ({
              name: a.agentName,
              ticketsResolved: a.ticketsResolved,
              avgResponseTime: a.avgResponseTime,
              satisfaction: a.customerSatisfaction || 0,
              role: a.role,
            }))
        : undefined;
      // dateRange is forwarded so the widget's caption names the window the
      // numbers actually cover (it already fetched with this range).
      return <AgentPerformanceWidget loading={loading} data={agentWidgetData} chartType={props.config?.chartType} title={props.config?.title} dateRange={props.dateRange} />;
    }

    case 'agent_workload': {
      // Current open load per agent (live snapshot). The whole roster is passed
      // through — NOT a top 5 — because a member holding nothing is a staffing
      // fact, and truncating the tail is exactly how that fact disappears. The
      // widget body scrolls.
      const workloadData = extra.agentData?.length
        ? [...extra.agentData]
            .sort((a: any, b: any) => (b.openTickets || 0) - (a.openTickets || 0))
            .map((a: any) => ({
              name: a.agentName,
              ticketsResolved: a.ticketsResolved,
              openTickets: a.openTickets || 0,
              avgResponseTime: a.avgResponseTime,
              satisfaction: a.customerSatisfaction || 0,
              role: a.role,
            }))
        : undefined;
      return <AgentPerformanceWidget loading={loading} data={workloadData} metric="open" chartType={props.config?.chartType} title={props.config?.title} />;
    }

    case 'my_performance': {
      // For an agent the metrics list is self-scoped, so the first row is them.
      const me = extra.agentData?.[0];
      return (
        <MyPerformanceWidget
          loading={loading}
          title={props.config?.title}
          data={me ? {
            ticketsResolved: me.ticketsResolved,
            openTickets: me.openTickets,
            avgResponseTime: me.avgResponseTime,
            // SLA & CSAT aren't computed per-agent in agent_metrics (the ticket
            // row has no *_sla_met / satisfaction_score columns), so they came
            // back 0/blank. Source the agent's own values from the self-scoped
            // dashboard metrics, which compute SLA from timestamps and CSAT from
            // surveys. Fall back to the agentData fields if absent.
            slaCompliance: dashboardData?.performance?.firstResponseSlaCompliance ?? me.slaCompliance,
            customerSatisfaction: dashboardData?.performance?.customerSatisfaction ?? me.customerSatisfaction,
          } : undefined}
        />
      );
    }

    case 'my_queue':
      return <MyQueueWidget loading={loading} title={props.config?.title} />;

    case 'recent_activity':
      return <RecentActivityWidget loading={loading} activities={extra.recentActivityData} title={props.config?.title} />;

    case 'automation_health':
      return <AutomationHealthWidget loading={loading} title={props.config?.title} />;

    case 'chart_resolution_source':
      // `resolutionSources` rides in on the dashboard metrics payload. Passing
      // undefined (rather than a zeroed object) when the field is absent lets the
      // widget show its error state instead of a confident "0%" — an older BFF
      // build that predates the field must not read as real data.
      return (
        <ResolutionSourceWidget
          loading={loading}
          title={normalizeResolutionSourceTitle(props.config?.title)}
          data={dashboardData?.resolutionSources}
        />
      );

    case 'custom_metric':
      return <CustomMetricWidget {...props} />;

    default:
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 gap-2">
          <AlertTriangle className="w-6 h-6" />
          <span className="text-sm">Unknown widget: {widgetType}</span>
        </div>
      );
  }
}

/** Determines if a widget type is a KPI card (no wrapper chrome needed) */
function isKpiWidget(type: string): boolean {
  return type.startsWith('kpi_');
}

const CHART_TYPE_LABELS: Record<string, string> = {
  area: 'Area',
  line: 'Line',
  bar: 'Bar',
  donut: 'Donut',
  pie: 'Pie',
  'horizontal-bar': 'H-Bar',
  gauge: 'Gauge',
};

export const WidgetRenderer = memo(function WidgetRenderer({
  widget,
  dashboardData,
  agentData,
  slaData,
  trendsData,
  recentActivityData,
  loading,
  dateRange,
  isEditMode,
  onRemove,
  onToggleVisibility,
  onConfigure,
  chartTypes,
  onChartTypeChange,
  drillDown,
}: WidgetRendererProps) {
  const palette = useThemeColors(PALETTE_TOKENS) as Palette;

  const commonProps: WidgetCommonProps = {
    config: { ...widget.config, title: widget.title },
    loading,
    dashboardData,
    dateRange,
    isEditMode,
    // Drill-down is a live-Dashboard behaviour: never while arranging widgets.
    ...(drillDown && !isEditMode ? { drillDown: { dateRange, teamId: drillDown.teamId } } : {}),
  };

  const isKpi = isKpiWidget(widget.widgetType);

  if (isKpi) {
    return (
      <div className="relative h-full group">
        {isEditMode && (
          <EditOverlay
            widgetId={widget.id}
            isVisible={widget.isVisible}
            onRemove={onRemove}
            onToggleVisibility={onToggleVisibility}
          />
        )}
        <WidgetErrorBoundary widgetName={widgetDisplayTitle(widget.widgetType, widget.title)}>
          {renderWidgetContent(widget.widgetType, commonProps, { agentData, slaData, dashboardData, trendsData, recentActivityData, palette })}
        </WidgetErrorBoundary>
      </div>
    );
  }

  return (
    <div className="relative h-full group">
      {isEditMode && (
        <EditOverlay
          widgetId={widget.id}
          isVisible={widget.isVisible}
          onRemove={onRemove}
          onToggleVisibility={onToggleVisibility}
          chartTypes={chartTypes}
          currentChartType={widget.config?.chartType}
          onChartTypeChange={onChartTypeChange}
        />
      )}
      <div className="h-full">
        <WidgetErrorBoundary widgetName={widgetDisplayTitle(widget.widgetType, widget.title)}>
          <Suspense fallback={<WidgetSkeleton />}>
            {renderWidgetContent(widget.widgetType, commonProps, { agentData, slaData, dashboardData, trendsData, recentActivityData, palette })}
          </Suspense>
        </WidgetErrorBoundary>
      </div>
    </div>
  );
});

/** Edit-mode overlay with drag handle, visibility toggle, remove, and inline chart type picker */
function EditOverlay({
  widgetId,
  isVisible,
  onRemove,
  onToggleVisibility,
  chartTypes,
  currentChartType,
  onChartTypeChange,
}: {
  widgetId: string;
  isVisible: boolean;
  onRemove?: (id: string) => void;
  onToggleVisibility?: (id: string, visible: boolean) => void;
  chartTypes?: string[];
  currentChartType?: string;
  onChartTypeChange?: (id: string, chartType: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const hasChartOptions = chartTypes && chartTypes.length > 1 && onChartTypeChange;

  return (
    <>
      {/* Top-right action buttons */}
      <div className="absolute top-1 right-1 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onToggleVisibility && (
          <button
            onClick={() => onToggleVisibility(widgetId, !isVisible)}
            className="p-1 rounded bg-gray-800/70 text-white hover:bg-gray-700/90 transition-colors"
            title={isVisible ? 'Hide widget' : 'Show widget'}
          >
            {isVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
        )}
        {hasChartOptions && (
          <button
            onClick={() => setPickerOpen(!pickerOpen)}
            className={`p-1 rounded transition-colors ${pickerOpen ? 'bg-primary-600 text-white' : 'bg-gray-800/70 text-white hover:bg-gray-700/90'}`}
            title="Change chart type"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        )}
        {onRemove && (
          <button
            onClick={() => onRemove(widgetId)}
            className="p-1 rounded bg-red-600/80 text-white hover:bg-red-500/90 transition-colors"
            title="Remove widget"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Inline chart type picker -- appears below the action buttons */}
      {pickerOpen && hasChartOptions && (
        <div className="absolute top-8 right-1 z-20 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 flex items-center gap-1.5">
          <span className="text-[10px] text-gray-500 mr-1 dark:text-gray-400">Chart:</span>
          {chartTypes.map(ct => (
            <button
              key={ct}
              onClick={() => {
                onChartTypeChange(widgetId, ct);
                setPickerOpen(false);
              }}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                (currentChartType || chartTypes[0]) === ct
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {CHART_TYPE_LABELS[ct] || ct}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function WidgetSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-4">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
      <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  );
}
